import type { AxiosError } from 'axios';
import { ApiError, type ApiEnvelope, type Page } from '@/types/api';

/** Symbol-keyed stash for pagination metadata on list responses. */
export const PAGE_SYMBOL = Symbol('page');

export interface WithPage {
  [PAGE_SYMBOL]?: Page;
}

/**
 * Unwrap the backend `{ status, code, message, data, page }` envelope.
 * Returns `data` on success (stashing `page` on a symbol for list hooks),
 * throws a typed {@link ApiError} otherwise.
 */
export function unwrap<T>(body: ApiEnvelope<T>): T {
  if (body && body.status === true) {
    const data = body.data;
    if (data && typeof data === 'object' && body.page) {
      try {
        (data as WithPage)[PAGE_SYMBOL] = body.page;
      } catch {
        /* frozen/primitive — ignore */
      }
    }
    return data;
  }
  throw new ApiError({
    message: body?.message || 'Request failed',
    code: body?.code ?? 400,
    payload: body,
  });
}

/** Read pagination metadata stashed by {@link unwrap}. */
export function getPage(data: unknown): Page | undefined {
  if (data && typeof data === 'object') {
    return (data as WithPage)[PAGE_SYMBOL];
  }
  return undefined;
}

interface BackendErrorBody {
  code?: number;
  message?: string;
  errorCode?: string;
  blocked?: boolean;
  data?: { code?: number; message?: string; errorCode?: string };
}

/**
 * A Joi validator 400 answers with a bare array of `{ message, path }` details
 * instead of the envelope, so it carries no top-level `message`. Read the
 * details so the cashier sees which field was refused, not axios's generic
 * "Request failed with status code 400".
 */
function validationMessage(body: unknown): string | null {
  if (!Array.isArray(body)) return null;
  const messages = body
    .map((d) => (d && typeof d === 'object' ? (d as { message?: unknown }).message : null))
    .filter((m): m is string => typeof m === 'string' && m.length > 0)
    .map((m) => m.replace(/"/g, ''));
  return messages.length ? `Data tidak valid: ${messages.join('; ')}` : null;
}

/** Convert an axios error into a typed {@link ApiError}, preserving errorCode. */
export function toApiError(error: unknown): ApiError {
  const axiosErr = error as AxiosError<BackendErrorBody>;

  if (axiosErr?.code === 'ERR_NETWORK' || axiosErr?.message === 'Network Error') {
    return new ApiError({
      message: 'Tidak dapat terhubung ke server.',
      code: 0,
      isNetwork: true,
    });
  }

  const status = axiosErr?.response?.status ?? 500;
  const body = axiosErr?.response?.data;
  const nested = body?.data ?? body;

  return new ApiError({
    message:
      nested?.message ||
      body?.message ||
      validationMessage(body) ||
      axiosErr?.message ||
      'Terjadi kesalahan.',
    code: nested?.code ?? body?.code ?? status,
    errorCode: nested?.errorCode ?? body?.errorCode ?? null,
    payload: body,
  });
}
